import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

export type AfishaHomeSearchBridge = {
  onSearch: (query: string) => void | Promise<void>;
  clearSearch: () => void;
  searchLoading: boolean;
  resetKey: number;
};

type AfishaHomeSearchBridgeContextValue = {
  bridge: AfishaHomeSearchBridge | null;
  setBridge: (bridge: AfishaHomeSearchBridge | null) => void;
  searchPanelOpen: boolean;
  setSearchPanelOpen: (open: boolean) => void;
};

const AfishaHomeSearchBridgeContext =
  createContext<AfishaHomeSearchBridgeContextValue | null>(null);

export const AfishaHomeSearchBridgeProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [bridge, setBridgeState] = useState<AfishaHomeSearchBridge | null>(
    null,
  );
  const [searchPanelOpen, setSearchPanelOpen] = useState(false);

  const setBridge = useCallback((next: AfishaHomeSearchBridge | null) => {
    setBridgeState(next);
  }, []);

  const value = useMemo(
    () => ({ bridge, setBridge, searchPanelOpen, setSearchPanelOpen }),
    [bridge, setBridge, searchPanelOpen],
  );

  return (
    <AfishaHomeSearchBridgeContext.Provider value={value}>
      {children}
    </AfishaHomeSearchBridgeContext.Provider>
  );
};

export const useAfishaHomeSearchBridge = () => {
  const ctx = useContext(AfishaHomeSearchBridgeContext);
  if (!ctx) {
    throw new Error(
      "useAfishaHomeSearchBridge must be used within AfishaHomeSearchBridgeProvider",
    );
  }
  return ctx;
};

export const useRegisterAfishaHomeSearch = (bridge: AfishaHomeSearchBridge) => {
  const { setBridge } = useAfishaHomeSearchBridge();

  React.useEffect(() => {
    setBridge(bridge);
    return () => setBridge(null);
  }, [bridge, setBridge]);
};
